import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Checkbox, FormControlLabel, MenuItem, TextField, Typography } from '@mui/material';
import PlaylistAddOutlinedIcon from '@mui/icons-material/PlaylistAddOutlined';
import DecimalField from '../../shared/DecimalField';
import { IconTile, TONE } from './builderUi';

const FIELD_SX = { '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' } };

/**
 * "Vapaa rivi": a line typed by hand for an email vendor (the vendor reads the code and
 * name from the email; no API would accept an unknown code). The purchase price is
 * VAT-exclusive by rule, so a plain DecimalField with the tag, never a VAT toggle.
 */
function FreeTextLineForm({ emailIntegrations, onAdd, onCancel, canViewPrices }) {
  const { t } = useTranslation();
  const [integrationId, setIntegrationId] = useState(emailIntegrations[0]?.id || '');
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [brand, setBrand] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [price, setPrice] = useState('');
  const [hide, setHide] = useState(false);

  const integration = emailIntegrations.find((i) => i.id === Number(integrationId));
  const valid = integration && (code.trim() || name.trim()) && parseFloat(quantity) > 0;

  const submit = () => {
    if (!valid) return;
    onAdd({ integration, code, name, brand, quantity, purchasePrice: price, hideFromRegister: hide });
  };

  return (
    <Box
      sx={{ mt: 1.5, p: { xs: 2, md: 2.25 }, borderRadius: '14px', border: '1px solid', borderColor: 'divider', bgcolor: 'background.default' }}
      data-testid="free-text-form"
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 0.75 }}>
        <IconTile size={28} tone={TONE.slate}>
          <PlaylistAddOutlinedIcon />
        </IconTile>
        <Typography sx={{ fontSize: 15, fontWeight: 700 }}>{t('orderBuilder.freeText.title')}</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.45 }}>
        {t('orderBuilder.freeText.help')}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        <TextField
          select
          size="small"
          label={t('orderBuilder.freeText.vendor')}
          value={integrationId}
          onChange={(e) => setIntegrationId(e.target.value)}
          sx={{ flex: '1 1 180px', ...FIELD_SX }}
          inputProps={{ 'data-testid': 'free-text-vendor' }}
        >
          {emailIntegrations.map((i) => (
            <MenuItem key={i.id} value={i.id}>
              {i.display_name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          label={t('orderBuilder.freeText.code')}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          sx={{ flex: '1 1 140px', ...FIELD_SX }}
          inputProps={{ 'data-testid': 'free-text-code' }}
        />
        <TextField
          size="small"
          label={t('orderBuilder.freeText.name')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ flex: '2 1 200px', ...FIELD_SX }}
          inputProps={{ 'data-testid': 'free-text-name' }}
        />
        <TextField
          size="small"
          label={t('orderBuilder.freeText.brand')}
          value={brand}
          onChange={(e) => setBrand(e.target.value)}
          sx={{ flex: '1 1 120px', ...FIELD_SX }}
        />
        <DecimalField
          size="small"
          label={t('orderBuilder.quantity')}
          value={quantity}
          onChange={setQuantity}
          sx={{ flex: '0 1 90px', ...FIELD_SX }}
          inputProps={{ 'data-testid': 'free-text-quantity' }}
        />
        {canViewPrices && (
          <DecimalField
            size="small"
            label={t('orderBuilder.freeText.price')}
            value={price}
            onChange={setPrice}
            sx={{ flex: '0 1 150px', ...FIELD_SX }}
            inputProps={{ 'data-testid': 'free-text-price' }}
            helperText={t('orderBuilder.exVatHelp')}
          />
        )}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
        <FormControlLabel
          control={<Checkbox size="small" checked={hide} onChange={(e) => setHide(e.target.checked)} />}
          label={<Typography sx={{ fontSize: 13.5 }}>{t('orderBuilder.freeText.hideFromRegister')}</Typography>}
        />
        <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
          <Button size="small" onClick={onCancel} sx={{ textTransform: 'none', color: 'text.secondary' }}>
            {t('Cancel')}
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={!valid}
            onClick={submit}
            sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none' }}
            data-testid="free-text-add"
          >
            {t('orderBuilder.addToCart')}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export default FreeTextLineForm;
